<template>
  <div ref="container">
    <AddNoteOrFile
      v-if="!formattedReadOnly.add"
      ref="addPartial"
      :hide-add-files="disableAddFiles"
      :hide-add-notes="disableAddNotes"
      :file-entries="fileEntries"
      :simple-note="simpleNote"
      hide-separator
      @add-note="(payload, done) => $emit('addNote', payload, done)"
      @save-attachments="(idsToUse, filesToRemove, done) => $emit('saveAttachments', idsToUse, filesToRemove, done)"
    >
      <slot name="buttons" />
    </AddNoteOrFile>

    <div
      v-if="!loading && groupedEntriesPaged.length > 0"
      class="timeline-container simple-mode"
      :class="{'read-only': formattedReadOnly.view, 'q-pt-md': !formattedReadOnly.add}"
    >
      <q-virtual-scroll
        key="entries"
        :items="groupedEntriesPaged"
        :virtual-scroll-slice-size="2"
        :scroll-target="scrollTargetEl"
        class="overflow-visible"
      >
        <template #default="{ item: obj, index }">
          <div :class="{'q-pb-sm': index !== groupedEntriesPaged.length, 'q-pt-lg': index !== 0, 'q-pt-sm': index === 0}">
            <div class="q-timeline__subtitle q-pt-xs">
              <span v-if="obj.date !== 'pinned'">{{ $formatDate(obj.date, luxonFormats.SHORT_WORDED_DATE) }}</span>
              <span v-else>Pinned</span>
            </div>

            <component
              :is="getTypeForEntry(item).component"
              v-for="(item, i) in obj.entries"
              :key="obj.date + '-' + item.id + '-' + i + '-' + item.timeline_type"
              :read-only="formattedReadOnly.view || !!item.timeline_morph"
              :disable-pinning="disablePinning"
              simple
              v-bind="getTypeForEntry(item).props(item)"
              @remove="(val) => removeEntry(item, val)"
              @save="(val) => saveEntry(val)"
              @pin="() => pinEntry(item)"
            />
          </div>
        </template>
      </q-virtual-scroll>
    </div>

    <div
      v-if="groupedEntries.length > groupedEntriesPaged.length"
      class="q-py-md"
    >
      <q-btn
        label="Show Older Results..."
        no-caps
        rounded
        color="blue-grey-7"
        class="full-width"
        @click="datePage++"
      />
    </div>

    <loading-spinner v-if="loading" />
    <no-results
      v-else-if="!hideNoEntriesText && groupedEntriesPaged.length < 1"
      padding="q-py-sm"
      without-icon
      align="left"
      title="No entries to display"
    />
  </div>
</template>

<script>
import timelineCommonMixin from 'components/partials/timeline/partials/timelineCommonMixin'

import AddNoteOrFile from 'components/partials/timeline/partials/AddNoteOrFile'
import TimelineGroup from 'components/partials/timeline/partials/TimelineGroup'

/**
 * SimpleTimeline
 *
 * The simple timeline is used for showing a simplified version of the main timeline.
 *
 * SLOTS:
 * - buttons: append buttons to the action bar at the top (only visible if note/file buttons are visible)
 *
 * EMITS/EVENTS:
 * ##### CALL doneFunc() ONCE REQUEST IS COMPLETED (WHERE APPLICABLE)
 *
 * - addNote (note, doneFumc): add a new note to the timeline and submit to backend
 * - updateNote (updatedNote, doneFunc): update an existing note and submit to backend
 * - removeNote (id): Remove a note from the timeline and submit to backend
 * - pinNote (id, pinState, doneFunc): pin/unpin note from the timeline and submit to backend
 *
 * - saveAttachments (ids): IDs of files to save to this timeline and submit to backend
 * - removeAttachment (id): Remove a single attachment from the timeline and submit to backend
 * - pinAttachment (id, pinState, doneFunc): pin/unpin a single attachment from the timeline and submit to backend
 *
 */
export default {
  components: {
    TimelineGroup, AddNoteOrFile
  },
  mixins: [timelineCommonMixin],
  props: {
    // Timeline array from backend
    timeline: {
      type: Array
    },
    // Force read-only over all elements on the timeline
    // true, false, or string (none, add, view, all)
    readOnly: {
      type: [String, Boolean],
      default: false
    },
    // Default scroll target for the timeline
    scrollTarget: {
      type: String,
      default: undefined
    },
    // Config / props to bind to each specific type
    config: {
      type: Object,
      default: () => {
        return {}
      }
    },
    // Disable button for adding new files to the timeline
    disableAddFiles: {
      type: Boolean,
      default: false
    },
    // Disable button for adding new note to the timeline
    disableAddNotes: {
      type: Boolean,
      default: false
    },
    // Disable pinning entries but don't treat element as read-only
    disablePinning: {
      type: Boolean,
      default: false
    },
    // Optionally hide the no results text
    hideNoEntriesText: {
      type: Boolean,
      default: false
    },
    // Display the add note button as a text field
    simpleNote: {
      type: Boolean,
      default: false
    },
    // Is the timeline loading
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['addNote', 'saveAttachments'],
  data () {
    return {
      datePage: 1,
      // All available categories/models which can be shown on the timeline
      partialTypes: this.basePartialTypes(this.config)
    }
  },
  computed: {
    scrollTargetEl () {
      try {
        return this.$refs.container.closest(this.scrollTarget)
      }
      catch (err) {
        return undefined
      }
    },
    // Go through all entries and add to any relevant data arrays
    formattedEntries () {
      const dateObj = {}
      const pinned = []
      const files = []

      // Main loop for splitting up the timeline entries appropriately
      for (const ele of (this.timeline || [])) {
        if (ele.timeline_type === 'attachment' && ele.key === 'attachments') {
          files.push(ele)
        }

        // If the string contains only a date, we don't want to convert to company timezone
        const formattedDate = ele.timeline_date.length <= 10
          ? ele.timeline_date
          : this.luxon.fromSQL(ele.timeline_date).toSQLDate()

        if (ele.timeline_pinned) {
          pinned.push(ele)
        }
        else {
          if (!dateObj[formattedDate]) {
            dateObj[formattedDate] = []
          }
          dateObj[formattedDate].push(ele)
        }
      }

      // Take this final object and convert to array for use by VirtualScroll
      // This loop is only for visible data which need to be grouped
      const dateArr = []
      for (const date in dateObj) {
        dateArr.push({
          date,
          entries: dateObj[date]
        })
      }

      if (pinned.length > 0) {
        dateArr.unshift({
          date: 'pinned',
          entries: pinned
        })
      }

      return {
        entries: dateArr,
        files
      }
    },
    // Timeline entries grouped by date (by company timezone)
    groupedEntries () {
      return this.formattedEntries.entries
    },
    groupedEntriesPaged () {
      return this.groupedEntries.slice(0, this.datePage * 10)
    },
    // Any files. Necessary for pushing and removing any attachments from this timeline
    fileEntries () {
      return this.formattedEntries.files
    },
    formattedReadOnly () {
      switch (this.readOnly) {
        default:
        case false:
          return { add: false, view: false }
        case 'add':
          return { add: true, view: false }
        case 'view':
          return { add: false, view: true }
        case true:
        case 'all':
          return { add: true, view: true }
      }
    }
  },
  methods: {
    getTypeForEntry (item) {
      return this.partialTypes.find(i => i.value === item.timeline_type)
    },
    // Request saveHandler if an entry is updated
    saveEntry (obj) {
      this.baseSaveEntry(this.partialTypes, obj)
    },
    // Request pinHandler if an entry is pinned/unpinned
    pinEntry (obj) {
      this.basePinEntry(this.partialTypes, obj)
    },
    // Request removeHandler if an entry is removed from the timeline
    removeEntry (obj, silent = false) {
      this.baseRemoveEntry(this.partialTypes, this.timeline, obj, silent)
    },
    focusOnNewNote () {
      if (this.$refs.addPartial) {
        this.$refs.addPartial.focusOnNewNote()
      }
    }
  }
}
</script>
